<template>
  <div class="bp-card-container">
    <div class="bp-top-container">
      <div class="bp-header-container">
        <img src="@shared/assets/img/logo/black-short-logo.png" class="logo" alt="SmartAC.com">
        <div class="bp-header">
          Smart Maintenance&reg; Edit Plan
        </div>
      </div>
      <button type="button" class="close-button" @click="toggleExitPopup(true)">
        <img src="@shared/assets/img/general/x-black.png" class="close-img" alt="Close">
      </button>
    </div>
    <div class="bp-body-container">
      <div class="bp-section-container">
        <div class="bp-section-body-container">
          <div
            v-if="partnerSettings.LOGO_URL"
            class="bp-logo-container"
            :style="{ backgroundColor: partnerSettings.PRIMARY_COLOR }"
          >
            <img v-if="partnerLogo" class="bp-logo" :src="partnerLogo" alt="Logo">
          </div>
          <PlanType
            editing
            :index="0"
            :active="activeStepIdx === 0"
            :changeActiveStepIdx="changeActiveStepIdx"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :toggleInfoModal="toggleInfoModal"
            :apponlyAllowed="partnerSettings.APPONLY_ALLOWED"
          />
          <NamePlan
            editing
            :index="1"
            :active="activeStepIdx === 1"
            :changeActiveStepIdx="changeActiveStepIdx"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
          <PlanPricing
            editing
            :index="2"
            :active="activeStepIdx === 2"
            :changeActiveStepIdx="changeActiveStepIdx"
            :corporation="partner"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :toggleInfoModal="toggleInfoModal"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
          <PlanDetails
            editing
            :index="3"
            :active="activeStepIdx === 3"
            :changeActiveStepIdx="changeActiveStepIdx"
            :partner="partner"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :toggleInfoModal="toggleInfoModal"
            :changeBuilderState="changeBuilderState"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
          <SignUpBonus
            editing
            :index="4"
            :active="activeStepIdx === 4"
            :changeActiveStepIdx="changeActiveStepIdx"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
          <ExclusionDisclaimer
            editing
            :index="5"
            :active="activeStepIdx === 5"
            :changeActiveStepIdx="changeActiveStepIdx"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :partnerSettings="partnerSettings"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
        </div>
        <div class="bp-footer-container">
          <button
            type="button"
            class="bp-footer-button"
            :disabled="finishedSection.length !== 6"
            @click="editPlan(true)"
          >
            Save and Exit
          </button>
        </div>
      </div>
      <div class="bp-plan-container">
        <div class="bp-body-header">
          *SmartAC.com recommends creating no more than 3 plans for customers
          <br>
          to choose from if you would like to use our
          <a href="/one-pager.pdf" target="_blank">
            One Pager
          </a>
          asset
        </div>
        <div class="flex-row align-start just-center">
          <PlanCard
            type="edit"
            :plan="plan"
            :finishedSection="finishedSection"
            :partnerSettings="partnerSettings"
            hideDetails
          />
          <ExclusionCard
            :plan="plan"
            :partnerSettings="partnerSettings"
          />
        </div>
      </div>
    </div>
    <InfoModal
      :showInfoModal="showInfoModal"
      :infoModalView="infoModalView"
      :close="() => toggleInfoModal(false)"
      :plan="plan"
    />
    <ConfirmExitPopup
      :show="showExitPopup"
      :close="() => toggleExitPopup(false)"
      :exit="close"
    />
    <ProcessOverlay
      :show="creatingPlan"
    />
  </div>
</template>

<script>
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';

import NamePlan from '@shared/components/partnerSettings/providerPlan/NamePlan.vue';
import PlanPricing from '@shared/components/partnerSettings/providerPlan/PlanPricing.vue';
import PlanType from '@shared/components/partnerSettings/providerPlan/PlanType.vue';
import PlanDetails from '@shared/components/partnerSettings/providerPlan/PlanDetails.vue';
import SignUpBonus from '@shared/components/partnerSettings/providerPlan/SignUpBonus.vue';
import ExclusionDisclaimer from '@shared/components/partnerSettings/providerPlan/ExclusionDisclaimer.vue';
import PlanCard from '@shared/components/partnerSettings/providerPlan/PlanCard.vue';
import ExclusionCard from '@shared/components/partnerSettings/providerPlan/ExclusionCard.vue';
import InfoModal from '@shared/components/partnerSettings/providerPlan/InfoModal.vue';
import ConfirmExitPopup from '@shared/components/partnerSettings/providerPlan/ConfirmExitPopup.vue';

export default {
  name: 'PlanBuilder',
  components: {
    NamePlan,
    PlanPricing,
    PlanType,
    PlanDetails,
    SignUpBonus,
    ExclusionDisclaimer,
    PlanCard,
    ExclusionCard,
    InfoModal,
    ConfirmExitPopup,
    ProcessOverlay,
  },
  props: {
    close: Function,
    selectedPlan: Object,
    partnerSettings: Object,
    partner: Object,
  },
  data() {
    return {
      activeStepIdx: 0,
      plan: {
        corpId: this.partner?.id,
        name: '',
        chargeFrequency: 'Monthly',
        priceInitial: null,
        priceSecond: null,
        priceAddon: null,
        creditAccrualMethod: null,
        comfortCreditEnabled: null,
        comfortCreditInitial: null,
        comfortCreditSecond: null,
        comfortCreditAddon: null,
        comfortCreditApplication: '',
        minRepairCost: null,
        redemptionRestriction: '',
        inspectionPerson: null,
        inspectionVirtual: null,
        inspectionFlex: null,
        bonusComfortCredit: null,
        bonusFilterVoucherFrequency: '',
        bonusFilterVoucher: null,
        voucherAccrualMethod: null,
        SignupBonusAccrual: null,
        services: [],
        status: 'Active',
        planType: 'Hardware',
      },
      finishedSection: ['planType', 'planName', 'planPricing', 'planDetail', 'signUpBonus', 'exclusionDisclaimer'],
      creatingPlan: false,
      showInfoModal: false,
      infoModalView: '',
      showExitPopup: false,
      partnerLogo: null,
    };
  },
  mounted() {
    const {
      LOGO_URL,
    } = this.partnerSettings;
    this.plan = this.selectedPlan;
    this.$store.dispatch('getProviderServices', {
      corpId: this.partner.id,
    });
    if (LOGO_URL) {
      this.loadImage(LOGO_URL);
    }
  },
  methods: {
    changeBuilderState(field, value) {
      if (this[field] !== undefined) {
        this[field] = value;
      }
    },
    changeActiveStepIdx(idx) {
      this.activeStepIdx = idx;
    },
    updatePlan(newPlan, finishedSection) {
      if (!this.finishedSection.includes(finishedSection)) {
        this.finishedSection = [
          ...this.finishedSection,
          finishedSection,
        ];
      }
      if (finishedSection === 'planType') {
        const planDetailIdx = this.finishedSection.indexOf('planDetail');
        if (planDetailIdx >= 0) {
          const newFinishedSection = this.finishedSection.slice();
          newFinishedSection.splice(planDetailIdx, 1);
          this.finishedSection = newFinishedSection;
        }
      }
      this.plan = newPlan;
    },
    markUnfinishedSection(section) {
      const newFinishedSection = this.finishedSection.slice();
      const sectionIdx = newFinishedSection.indexOf(section);
      if (sectionIdx >= 0) {
        newFinishedSection.splice(sectionIdx, 1);
      }
      this.finishedSection = newFinishedSection;
    },
    markFinishedSection(section) {
      if (!this.finishedSection.includes(section)) {
        this.finishedSection = [
          ...this.finishedSection,
          section,
        ];
      }
    },
    async editPlan() {
      this.creatingPlan = true;
      try {
        await this.$store.dispatch('updatePlanStatus', this.plan);
        this.creatingPlan = false;
        this.$store.dispatch('showSuccessToast', {
          message: `Success! ${this.plan.name} plan has been updated`,
        });
        if (this.plan.disclaimer !== this.partnerSettings.PLAN_DISCLAIMER) {
          this.$store.dispatch('getPartnerDetail', { corpId: this.partner.id });
        }
        this.close();
      } catch (err) {
        this.$store.dispatch('showAlertToast', {
          message: 'There was an unexpected error updating plan. Please try again or contact support',
        });
        this.creatingPlan = false;
      }
    },
    toggleInfoModal(show, view) {
      if (show) {
        this.showInfoModal = show;
        this.infoModalView = view;
      } else {
        this.showInfoModal = show;
      }
    },
    toggleExitPopup(show) {
      this.showExitPopup = show;
    },
    async loadImage(imageUrl) {
      this.partnerLogo = await this.$store.dispatch('getImageResponse', {
        url: imageUrl,
        logo: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>

.bp-card-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: Poppins;

  @media (min-width: 1700px) {
    font-size: 120%;
  }

  .bp-top-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 63px;
    padding: 0 30px;
    background-color: #E0E8FF;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.08);

    .bp-header-container {
      display: flex;
      align-items: center;

      .logo {
        width: 35px;
        margin-right: 24px;
      }

      .bp-header {
        font-size: 1.25em;
        font-weight: 500;
        color: $NAVY100;
      }
    }

    .close-button {
      width: 28px;
      height: 28px;
      border-radius: 4px;
      background-color: $N5;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;

      .close-img {
        width: 10px;
        height: 10px;
      }
    }
  }

  .bp-body-container {
    flex: 1;
    width: 100%;
    height: 100%;
    display: flex;
    overflow-y: auto;

    .bp-section-container {
      display: flex;
      flex-direction: column;
      flex: 1;
      height: 100%;
      overflow-y: auto;
      position: relative;

      .bp-section-body-container {
        padding: 24px 73px 100px 61px;
        flex: 1;

        .bp-logo-container {
          height: 80px;
          width: 80px;
          border-radius: 6px;
          padding: 4px;
          margin-bottom: 17px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: auto;
          margin-right: auto;

          .bp-logo {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }
      }

      .bp-footer-container {
        position: sticky;
        bottom: 0;
        left: 0;
        right: 0;
        min-height: 74px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $WHITE;
        border-top: 1px solid $N5;

        .bp-footer-button {
          width: 165px;
          height: 24px;
          font-size: 0.625em;
          color: $WHITE;
          background-color: $NBLUE100;
          border-radius: 6px;
          white-space: nowrap;

          &[disabled] {
            background-color: $N40;
          }

          &:first-child {
            margin-right: 28px;
          }

          @media (min-width: 1700px) {
            width: 170px;
            height: 36px;
          }
        }
      }
    }

    .bp-plan-container {
      flex: 1;
      background-image: url('@shared/assets/img/setting/plan-popup-bg.png');
      background-size: 100% 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 66px;

      .bp-body-header {
        text-align: center;
        color: $N70;
        font-size: 0.875em;
        margin-bottom: 24px;

        a {
          font-weight: 500;
          color: $NBLUE100;
          text-decoration: underline;
        }
      }
    }

    .bp-disclaimer-container {
      background-color: $WHITE;
      border-radius: 14px;
      width: 350px;
      min-height: 400px;
      max-height: 700px;
      display: flex;
      flex-direction: column;
      .bp-disclaimer-header {
        height: 37px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-bottom: 1px solid $N10;
        font-size: 1em;
        font-weight: 500;
        color: $N80;
      }

      .bp-disclaimer-body-container {
        padding: 10px 32px;

        .bp-disclaimer-body-header {
          font-size: 0.875em;
          color: $N40;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .bp-disclaimer-body {
          font-size: 0.75em;
          color: $N40;
          margin-bottom: 8px;
        }

        .bp-disclaimer-body-title {
          margin-top: 16px;
          margin-bottom: 4px;
          color: $N80;
          font-weight: 500;
          font-size: 0.875em;
        }

        .bp-disclaimer-body-text {
          font-size: 0.75em;
          color: $N80;
        }
      }
    }
  }
}
</style>
